import { ggVars, toCamelCase } from 'constants/ggVars';

import { isPlainObject } from 'lodash';

import { CONSTRAINT_LEVELS, DEFAULT_OPTIMIZATION } from './constants';
import { createInitialState } from './reducer';
import {
  AdvancedOptions,
  AgeRange,
  ConstraintLevel,
  FormMode,
  InheritedParams,
  NewReferenceFlavorOption,
  OptimizationSettings,
  ProductSelection,
  ReportRequestState,
  SmokingHabits,
  UpdateSource,
  UpdateStrategy,
} from './types';

/** The report row the Clone / Update menu items pass through router state. */
export interface TemplateReport {
  reportId: string;
  jobId: string;
  startedAt: string;
  projectName: string;
  clientName: string;
  targetGroupName: string;
  workspaceId: number;
  reportType: string;
  producerByWorkspaceId?: { name: string } | null;
  params: Record<string, unknown> | null;
}

/*
 * Params in the database round-trip through R: NULL becomes `{}` and
 * length-one vectors are unboxed to scalars. These helpers undo that.
 */
const isRNull = (v: unknown) =>
  v === null || v === undefined || (isPlainObject(v) && Object.keys(v as object).length === 0);

export const asArray = <T = string>(v: unknown): T[] => {
  if (isRNull(v)) return [];
  return (Array.isArray(v) ? v : [v]) as T[];
};

export const asScalar = <T>(v: unknown, fallback: T): T => {
  if (isRNull(v)) return fallback;
  if (Array.isArray(v)) return v.length > 0 ? (v[0] as T) : fallback;
  return v as T;
};

const asBool = (v: unknown, fallback: boolean): boolean => {
  const value = asScalar<unknown>(v, fallback);
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
};

const asNumber = (v: unknown, fallback: number): number => {
  const n = Number(asScalar<unknown>(v, fallback));
  return Number.isFinite(n) ? n : fallback;
};

export const ageRangesFromParams = (v: unknown): AgeRange[] =>
  asArray<Record<string, unknown>>(v)
    .filter(isPlainObject)
    .map((r) => ({
      min: asNumber(r.min, 18),
      max: asNumber(r.max, 65),
      fraction: asNumber(r.fraction, 1),
    }));

export const inheritedFromParams = (
  params: Record<string, unknown>,
): InheritedParams => ({
  projectName: asScalar<string>(params.project_name, ''),
  countries: asArray<string>(params.countries),
  ageRanges: ageRangesFromParams(params.age_ranges),
  includeMale: asNumber(params.include_male, 0.5),
  includeFemale: asNumber(params.include_female, 0.5),
  sansRejector: asBool(params.SansRejector, false),
  customClassDbName: asScalar<string | null>(params.custom_class_db_name, null),
  competitiveSetFolders: asArray<string>(params.competitive_set_folders),
  competitiveSetProducts: asArray<string>(params.competitive_set_products),
});

const SMOKING: SmokingHabits[] = ['All', 'Smokers', 'Non-Smokers'];

const ADVANCED_KEYS: Array<keyof AdvancedOptions> = [
  'include_texture',
  'texture_data_only',
  'texture_only',
  'showConfidence',
  'show_TC_se',
  'use_hyperlocal_glp',
  'combine_artificial_sweeteners',
  'wipe_out_imputed_ggvars',
  'bootstrap_pq',
  'plotNorm',
  'generate_ppt',
  'profilingEnabled',
  'includeNoveltyScores',
  'experimental_features',
  'only_include_parents',
  'include_archetypes',
];

const advancedFromParams = (
  params: Record<string, unknown>,
  defaults: AdvancedOptions,
): AdvancedOptions =>
  ADVANCED_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: asBool(params[key], defaults[key]) }),
    { ...defaults },
  );

const GGVAR_KEYS = new Set(ggVars.map((g) => toCamelCase(g.key)));

export const optimizationFromParams = (
  params: Record<string, unknown>,
): OptimizationSettings => {
  const ggvars = asArray<string>(params.ggvar_to_lock);
  const mins = asArray<number>(params.ggvar_min_list);
  const maxs = asArray<number>(params.ggvar_max_list);
  const tune = asArray<string>(params.rangeReff);
  const range = (isPlainObject(params.rangeReffRange)
    ? params.rangeReffRange
    : {}) as Record<string, unknown>;
  const tuneMin = asArray<number>(range.min);
  const tuneMax = asArray<number>(range.max);
  const levels = asArray<string>(params.constraint_levels);
  const nrf = asArray<unknown>(params.new_reference_flavors_allowed).map((v) =>
    String(v).toUpperCase(),
  );
  const pixels = asArray<string>(params.pixel_list);
  return {
    ...DEFAULT_OPTIMIZATION,
    targetProduct: asScalar<string | null>(params.products_to_opt, null),
    ggvarConstraints: ggvars
      .filter((k) => GGVAR_KEYS.has(k))
      .map((key) => {
        const i = ggvars.indexOf(key);
        return { key, min: Number(mins[i] ?? 0), max: Number(maxs[i] ?? 5) };
      }),
    refFlavorsToLock: asArray<string>(params.ref_flavor_to_lock),
    refFlavorsToRemove: asArray<string>(params.flavors_to_remove),
    refFlavorsToTune: tune.map((flavor, i) => ({
      flavor,
      min: Math.round(Number(tuneMin[i] ?? 0) * 100),
      max: Math.round(Number(tuneMax[i] ?? 0) * 100),
    })),
    constraintLevels: CONSTRAINT_LEVELS.map((c) => c.level).filter(
      (level): level is ConstraintLevel => levels.includes(level),
    ),
    newReferenceFlavors: (['TRUE', 'FALSE'] as NewReferenceFlavorOption[]).filter(
      (v) => nrf.includes(v),
    ),
    optimizeFlavor: asBool(params.run_flavor_opt, true),
    optimizeTexture: asBool(params.run_texture_opt, false),
    showNorms: asBool(params.show_pg_gNorm, false),
    tuningPlots: asBool(params.show_tuning_plots, true),
    plotNorm: asBool(params.plotNorm, false),
    pixelList: pixels.join('\n'),
  };
};

export const updateSourceFromTemplate = (
  template: TemplateReport,
): UpdateSource => {
  const params = template.params ?? {};
  const inherited = inheritedFromParams(params);
  return {
    reportId: template.reportId,
    jobId: template.jobId,
    startedAt: template.startedAt,
    targetGroupName: template.targetGroupName,
    originalProducts: inherited.competitiveSetProducts,
    originalWorkspaces: asArray<string>(params.client_allowed_workspaces),
    originalCategories: inherited.competitiveSetFolders,
    countries: inherited.countries,
    ageRanges: inherited.ageRanges,
    includeMale: inherited.includeMale,
    includeFemale: inherited.includeFemale,
    sansRejector: inherited.sansRejector,
  };
};

/** Data the template cannot supply on its own and must be looked up. */
export interface TemplateFollowUp {
  /** Product names to resolve against miniGLP. */
  productNames: string[];
  /** Mark resolved products as already part of the report (update mode). */
  productsAreOriginal: boolean;
  /** Optimization clones: the parent job to look up, and settings to restore once it loads. */
  parentJobId: string | null;
  optimization: OptimizationSettings | null;
  mixAndMatch: Array<{ reportId: string; artifactPath: string; versions: string[] }>;
}

/** Re-running an update keeps its strategy; anything else defaults to version-controlled translation. */
const strategyFromParams = (params: Record<string, unknown>): UpdateStrategy => {
  if (params.report_type !== 'update_products') return 'version_controled_translation';
  if (asBool(params.version_controled_translation, false))
    return 'version_controled_translation';
  if (asBool(params.ensure_exact_pq, false)) return 'ensure_exact_pq';
  return 'standard';
};

const isMixAndMatch = (params: Record<string, unknown>) =>
  asArray(params.mix_and_match).length > 0;

export const stateFromTemplate = (
  template: TemplateReport,
  mode: FormMode,
): { state: ReportRequestState; followUp: TemplateFollowUp } => {
  const params = template.params ?? {};
  const base = createInitialState(mode);
  const workspaceName = template.producerByWorkspaceId?.name ?? '';
  const ageRanges = ageRangesFromParams(params.age_ranges);
  const smoking = asScalar<string>(params.smoking_habits, 'All');

  const common: Partial<ReportRequestState> = {
    projectName: template.projectName ?? '',
    clientName: template.clientName ?? '',
    workspace: template.workspaceId
      ? { id: template.workspaceId, name: workspaceName }
      : null,
    allowedWorkspaces: asArray<string>(params.client_allowed_workspaces),
    categories: asArray<string>(params.competitive_set_folders),
    customClassDbName: asScalar<string>(params.custom_class_db_name, '') ?? '',
    customClassDbProducts: asArray<string>(params.custom_class_db_products),
    advanced: advancedFromParams(params, base.advanced),
    reportIdToInherit: asScalar<string>(
      params.report_id_to_inherit_parent_data_sources,
      '',
    ) ?? '',
  };

  const followUp: TemplateFollowUp = {
    productNames: [],
    productsAreOriginal: false,
    parentJobId: null,
    optimization: null,
    mixAndMatch: [],
  };

  if (mode === 'update') {
    const updateSource = updateSourceFromTemplate(template);
    return {
      state: {
        ...base,
        ...common,
        reportKind: 'update_products',
        updateSource,
        updateStrategy: strategyFromParams(params),
      },
      followUp: {
        ...followUp,
        productNames: updateSource.originalProducts,
        productsAreOriginal: true,
      },
    };
  }

  if (template.reportType === 'optimization') {
    const mixAndMatch = isMixAndMatch(params);
    return {
      state: {
        ...base,
        reportKind: mixAndMatch ? 'mix_and_match' : 'optimization',
        reportIdToInherit: common.reportIdToInherit,
      },
      followUp: {
        ...followUp,
        parentJobId: asScalar<string | null>(params.parent_job_id, null),
        optimization: optimizationFromParams(params),
        mixAndMatch: mixAndMatch
          ? asArray<Record<string, unknown>>(params.mix_and_match).map((m) => ({
              reportId: asScalar<string>(m.report_id, ''),
              artifactPath: asScalar<string>(m.artifact_path, ''),
              versions: asArray<string>(m.versions),
            }))
          : [],
      },
    };
  }

  const country = asScalar<string | null>(params.countries, null);
  return {
    state: {
      ...base,
      ...common,
      reportKind: 'market_survey',
      country: country ? String(country).trim() : null,
      clts: asArray<string>(params.clts),
      includeFemale: asNumber(params.include_female, 0.5),
      ageCategory:
        ageRanges.length > 0 && ageRanges.every((r) => r.max <= 20)
          ? 'Children'
          : 'Adult',
      ageRanges: ageRanges.length > 0 ? ageRanges : base.ageRanges,
      smokingHabits: SMOKING.includes(smoking as SmokingHabits)
        ? (smoking as SmokingHabits)
        : 'All',
      sansRejector: asBool(params.SansRejector, true),
      ethnicities: {
        asian: asBool(params.asian, true),
        black: asBool(params.black, true),
        hispanic: asBool(params.hispanic, true),
        white: asBool(params.white, true),
      },
    },
    followUp: {
      ...followUp,
      productNames: asArray<string>(params.competitive_set_products),
    },
  };
};

/**
 * Resolves template product names to catalog rows. A name can exist in many
 * workspaces; only rows in the template's allowed workspaces are kept so a
 * clone shows the products the report actually used. A name with no row in
 * those workspaces keeps all its rows so the user can still choose one.
 */
export const resolveTemplateProducts = (
  names: string[],
  byName: Map<string, ProductSelection[]>,
  allowedWorkspaces: string[],
  original = false,
): { rows: ProductSelection[]; missing: string[] } => {
  const allowed = new Set(allowedWorkspaces);
  const missing: string[] = [];
  const rows = names.flatMap((name) => {
    const matches = byName.get(name);
    if (!matches?.length) {
      missing.push(name);
      return [];
    }
    const inAllowed = matches.filter((p) => allowed.has(p.workspace));
    return (inAllowed.length ? inAllowed : matches).map((p) => ({ ...p, original }));
  });
  return { rows, missing };
};
